/** Pure rules of the app shell (no React): current project, breadcrumbs, identity lines and nav active state. */
import type { Me, Project } from '@hangar/shared';
import type { ApiError } from '@/api/client';
import { NAV, SECTION_LABEL, SECTION_SLUG, sectionOf, type NavItem, type Section } from './nav';

export interface Crumb {
  label: string;
  /** Project slug when the crumb links to a project page. */
  projectSlug?: string;
  /** Absolute path when the crumb links somewhere else (e.g. the Builds list from a build). */
  to?: string;
  home?: boolean;
}

/** The project in the URL; with no slug in the URL, the first project; an unknown slug has no project. */
export function pickProject<P extends { slug: string }>(projects: P[] | undefined, slug: string | undefined): P | undefined {
  return projects?.find((p) => p.slug === slug) ?? (slug ? undefined : projects?.[0]);
}

/**
 * No floating Run build where it covers the page's own controls or has nothing to do: the framed architecture map, the
 * tutorial player and the Clients pages of Hangar administration (a client's danger zone sits at the bottom).
 */
const NO_RUN_BUILD_FAB: readonly Section[] = ['architecture', 'tutorials', 'clients', 'platform-providers'];
export const showsRunBuildFab = (section: Section): boolean => !NO_RUN_BUILD_FAB.includes(section);

/** Sections whose pages are not about one project, so the trail has no project crumb. */
const PROJECTLESS_SECTIONS: readonly Section[] = ['integrations', 'overview', 'getting-started', 'access', 'usage', 'clients', 'platform-providers', 'tutorials', 'architecture', 'devices'];

export interface CrumbInput {
  orgName: string | undefined;
  project: Pick<Project, 'name' | 'slug'> | undefined;
  section: Section;
  buildId: string | undefined;
  /** The App page (design 29) ends the trail with the app's slug: Malible / StoreAI / storeai-mobile. */
  app: string | undefined;
}

function projectCrumbs(project: CrumbInput['project'], section: Section): Crumb[] {
  return project && !PROJECTLESS_SECTIONS.includes(section) ? [{ label: project.name, projectSlug: project.slug }] : [];
}

function tailCrumbs({ project, section, buildId, app }: CrumbInput): Crumb[] {
  if (section === 'builds' && buildId && project) return [{ label: 'Builds', to: `/projects/${project.slug}/builds` }, { label: 'Build' }];
  return [{ label: app || SECTION_LABEL[section] }];
}

/** Organization / project / page, for the top bar. */
export function buildCrumbs(input: CrumbInput): Crumb[] {
  return [{ label: input.orgName ?? 'Organization', home: true }, ...projectCrumbs(input.project, input.section), ...tailCrumbs(input)];
}

export type CrumbKind = 'page' | 'link' | 'project' | 'home' | 'text';

/** How a crumb renders: the last one is the current page; the rest link by `to`, project slug, or home. */
export function crumbKind(c: Crumb, last: boolean): CrumbKind {
  if (last) return 'page';
  if (c.to) return 'link';
  if (c.projectSlug) return 'project';
  return c.home ? 'home' : 'text';
}

/** Display name and email of the signed-in actor (service tokens have no user). */
export function identityOf(me: Me | undefined): { name: string; email: string } {
  return { name: me?.user?.name ?? me?.actor.label ?? '', email: me?.user?.email ?? '' };
}

/** The phone top bar's two lines: the project (else the org, else the product) and "<org> · v<version>". */
export function topBarTitle(project: Pick<Project, 'name'> | undefined, me: Me | undefined): string {
  return project?.name ?? me?.org.name ?? 'Hangar';
}

type PendingInvitation = NonNullable<Me['pendingInvitations']>[number];

/** Invitations the org switcher offers to join (SPEC decision 22): none during a client preview or from an older API. */
export function pendingInvitationsOf(me: Me | undefined): PendingInvitation[] {
  return me && !me.preview ? (me.pendingInvitations ?? []) : [];
}

/**
 * The orgs the switcher menu lists: every org of the user once there is something to choose (another org or an
 * invitation), else none, and the row only names the current org.
 */
export function switcherOrgs(me: Me | undefined): NonNullable<Me['orgs']> {
  if (!me || me.preview) return [];
  const orgs = me.orgs ?? [];
  if (orgs.length > 1) return orgs;
  return pendingInvitationsOf(me).length ? (orgs.length ? orgs : [{ ...me.org, role: me.orgRole ?? 'viewer' }]) : [];
}

/** "Join Acme as a member": the invitation's menu line. */
export const joinLabel = (p: PendingInvitation): string => `Join ${p.org.name} as ${/^[aeiou]/.test(p.orgRole) ? 'an' : 'a'} ${p.orgRole}`;

export function orgLabel(me: Me | undefined): string {
  return me ? me.org.name : '—';
}

/**
 * Whether the nav entry `slug` is the current one: the section of the path decides (the same rule as the breadcrumb),
 * never the entry's link. With no project yet every project entry (Environment variables, Credentials, Builds, Settings)
 * links to /projects, so matching links lit all of them up together with Apps & profiles. An App page
 * (/projects/:slug/apps/:app) belongs to Apps & profiles; a build's page to Builds.
 */
export function isCurrentEntry(slug: string, pathname: string): boolean {
  return SECTION_SLUG[sectionOf(pathname)] === slug;
}

export const TAB_SLUGS = ['overview', 'builds', 'apps'] as const;

/** The three phone tabs, in tab order. */
export function tabItems(): NavItem[] {
  return TAB_SLUGS.map((slug) => NAV.find((i) => i.slug === slug)!);
}

/** `GET /me` refused the signed-in identity (401/403): the shell shows the no-access page instead of loading forever. */
export function isAccessRefused(error: unknown): error is ApiError {
  // Checked by shape: this module stays free of the API client (and of `window`), as its tests run without a DOM.
  if (!(error instanceof Error) || error.name !== 'ApiError') return false;
  const { status } = error as ApiError;
  return status === 401 || status === 403;
}

/** The API refused the org itself: it is suspended (`HGR-ORG-SUSPENDED`, SPEC §17.6); a page of its own, never a retry. */
export function isOrgSuspended(error: unknown): error is ApiError {
  if (!(error instanceof Error) || error.name !== 'ApiError') return false;
  const { status, code } = error as ApiError;
  return status === 403 && code === 'HGR-ORG-SUSPENDED';
}

/** Run build's tooltip: what the button starts, so it reads as the action whether or not it is the screen's accent. */
export function runBuildHint(projectName?: string): string {
  return projectName ? `Start a build in ${projectName}: pick the app, build profile and branch` : 'Start a build: pick the project, app, build profile and branch';
}
